import { Stack } from '@mui/material';
import {
  AgeGroup,
  CustomField,
  FilterElementMessageCriteria,
  GroupSubject,
  Individual,
  MedicalNeedCriteriaData,
  BooleanOperator,
  CustomFieldDataType,
  CustomFieldFilter,
  DateOperator,
  MedicalNeedFilter,
  MessageCriteria,
  MessageCriteriaData,
  MessageCriteriaType,
  SelectListOperator,
  MessageCriterion,
  Recipient,
  SchoolLevel,
  SchoolProperty,
  SimpleListResult,
  TextOperator,
  CustomFieldCriteriaData,
} from '@schooly/api';
import { Genders, Nationalities, PROPERTIES_TEXT_IDS, PropertiesTextIds } from '@schooly/constants';
import { Bubble } from '@schooly/style';
import React, { PropsWithChildren } from 'react';

import { SchoolUserRole } from '../../constants/school';
import { MessageCriteriaItem, MessageFormCriteria } from '../../context/messages/MessageContext';
import getTypedObjectKeys from '../../utils/getTypedObjectKeys';

export const isMessageCriteriaWithFilterElement = (
  criteria: MessageCriteria,
): criteria is FilterElementMessageCriteria => criteria.filter_element !== null;

function getCriteriaItemName(
  criteria: MessageCriteriaItem,
  formatMessage: (descriptor: { id: string }) => string,
): string {
  switch (criteria.category) {
    case 'gender':
      return Number.isInteger(criteria.enum_index) ? Genders[criteria.enum_index!] : '';
    case 'nationality':
      return Number.isInteger(criteria.enum_index) ? Nationalities[criteria.enum_index!] : '';
    case 'subject':
      return criteria.subject?.name ?? '';
    case 'group':
      return criteria.group?.name ?? '';
    case 'expired':
      return formatMessage?.({ id: PROPERTIES_TEXT_IDS.expired }) ?? '';
    case 'medical_needs':
      return formatMessage({ id: 'peopleDetail-AllergyMedicalConcerns' });
    case 'custom_fields':
      return criteria.custom_field?.name ?? '';
    default:
      return criteria.school_property?.name ?? '';
  }
}

function getCriteriaItemKey(criteria: MessageCriteriaItem): string {
  switch (criteria.category) {
    case 'subject':
      return criteria.subject?.id ?? '';
    case 'group':
      return criteria.group?.id ?? '';
    case 'gender':
    case 'nationality':
      return `${criteria.category}-${Number.isInteger(criteria.enum_index) ? criteria.enum_index! : ''}`;
    case 'expired':
    case 'medical_needs':
      return criteria.category;
    case 'custom_fields':
      return criteria.custom_field?.id ?? '';
    default:
      return criteria.school_property?.id ?? '';
  }
}

export function getCriteriaByCategory(
  criteria: MessageCriteriaItem[],
  formatMessage: (descriptor: { id: string }) => string,
) {
  return criteria.reduce(
    (
      acc: Partial<Record<PropertiesTextIds, MessageCriteriaItem[]>>,
      criteria: MessageCriteriaItem,
    ) => {
      const { category } = criteria;
      const duplicateName = acc[category]?.find(
        (c) =>
          getCriteriaItemName(c, formatMessage) === getCriteriaItemName(criteria, formatMessage),
      );

      if (duplicateName) {
        return acc;
      }

      if (acc[category]) {
        acc[category]!.push(criteria);

        return acc;
      }

      return { ...acc, [category]: [criteria] };
    },
    {},
  );
}

interface GetDisplayedCriteriaParams extends PropsWithChildren {
  criteria: MessageCriteriaItem[];
  formatMessage: (descriptor: { id: string }) => string;
}

export const getDisplayedCriteria = ({
  criteria,
  children,
  formatMessage,
}: GetDisplayedCriteriaParams) => {
  const criteriaByCategory = getCriteriaByCategory(criteria, formatMessage);

  const displayedCategories = getTypedObjectKeys(criteriaByCategory);
  const displayedCategoriesWithGrouping = displayedCategories.filter(
    (c) => criteriaByCategory[c]?.[0]?.propGroup,
  );

  if (!criteriaByCategory || !displayedCategories.length) {
    return null;
  }

  const notEmptyCategories = getTypedObjectKeys(PROPERTIES_TEXT_IDS).filter((c) =>
    displayedCategories.includes(c),
  );

  if (displayedCategoriesWithGrouping.length) {
    displayedCategoriesWithGrouping.forEach((key) => {
      const criteriaList = [...(criteriaByCategory[key] ?? [])];
      criteriaByCategory[key] = getSelectedCriteriaWithGrouping(criteriaList);
    });
  }

  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" rowGap={1}>
      {children}
      {notEmptyCategories.map((category, _, array) => (
        <React.Fragment key={category}>
          {array.length > 1 && category !== array[0] && (
            <span className="AvailableCriteriaCard__separator">,</span>
          )}
          {criteriaByCategory[category]?.map((criteria, criteriaIndex) => {
            return (
              <React.Fragment key={getCriteriaItemKey(criteria)}>
                <Bubble
                  disableHover
                  size="medium"
                  tone="gray"
                  label={
                    criteria.propGroupItems?.length
                      ? criteria.propGroup?.name
                      : getCriteriaItemName(criteria, formatMessage)
                  }
                />
                {criteriaIndex <
                  (criteriaByCategory[category] as MessageCriteriaItem[])!.length - 1 && (
                  <span className="AvailableCriteriaCard__separator">+</span>
                )}
              </React.Fragment>
            );
          })}
        </React.Fragment>
      ))}
    </Stack>
  );
};

export const getParentsFromIndividuals = (individuals?: Individual[]) =>
  individuals?.filter(
    (i) =>
      i.recipient_type === SchoolUserRole.Parent && i.individual_type === SchoolUserRole.Parent,
  );

export const getStaffFromIndividuals = (individuals?: Individual[]) =>
  individuals?.filter(
    (i) => i.recipient_type === SchoolUserRole.Staff && i.individual_type === SchoolUserRole.Staff,
  );

export const getParentsOfStudentsFromIndividuals = (individuals?: Individual[]) =>
  individuals?.filter(
    (i) =>
      i.recipient_type === SchoolUserRole.Parent && i.individual_type === SchoolUserRole.Student,
  );

export const getStaffOfStudentsFromIndividuals = (individuals?: Individual[]) =>
  individuals?.filter(
    (i) =>
      i.recipient_type === SchoolUserRole.Staff && i.individual_type === SchoolUserRole.Student,
  );

export const getActualParentsRecipient = (individuals?: Recipient[]) =>
  individuals?.filter((i) => i.school_user_relation_role === SchoolUserRole.Parent);

export const getActualStaffRecipient = (individuals?: Recipient[]) =>
  individuals?.filter((i) => i.school_user_relation_role === SchoolUserRole.Staff);

export const convertIndividualToUserSearchResult = (
  individual: Individual | Recipient,
): SimpleListResult => {
  const { school_user_relation } = individual;

  return {
    ...school_user_relation,
    relation_id: school_user_relation.relation_id!,
    user_id: school_user_relation.user_id!,
  };
};

export const getParentsOfStudentsCriteria = (criteria?: MessageCriteria[]) =>
  criteria?.filter(
    (c) =>
      c.criterion_type === MessageCriterion.Parents && c.recipient_type === SchoolUserRole.Parent,
  );

export const getStaffCriteria = (criteria?: MessageCriteria[]) =>
  criteria?.filter(
    (c) => c.criterion_type === MessageCriterion.Staff && c.recipient_type === SchoolUserRole.Staff,
  );

const categoryMap: Record<number, PropertiesTextIds> = {
  [MessageCriteriaType.Status]: 'status',
  [MessageCriteriaType.House]: 'house',
  [MessageCriteriaType.Department]: 'department',
  [MessageCriteriaType.Campus]: 'campus',
};

interface GetItemsFromFilterElementParams {
  el: FilterElementMessageCriteria['filter_element'];
  properties: SchoolProperty[];
  subjects: GroupSubject[];
  ageGroups: AgeGroup[];
  schoolLevels: SchoolLevel[];
}

const getItemsFromFilterElement = ({
  el,
  properties,
  subjects,
  ageGroups,
  schoolLevels,
}: GetItemsFromFilterElementParams): MessageCriteriaItem[] => {
  switch (el.type) {
    case MessageCriteriaType.Gender:
      return (
        el.element_enum_index?.map((idx) => ({ category: 'gender' as const, enum_index: idx })) ??
        []
      );

    case MessageCriteriaType.Nationality:
      return (
        el.element_enum_index?.map((idx) => ({
          category: 'nationality' as const,
          enum_index: idx,
        })) ?? []
      );

    case MessageCriteriaType.Subject:
      return (
        el.element_id?.reduce<MessageCriteriaItem[]>((acc, id) => {
          const subj = subjects?.find((s) => s.id === id);
          if (subj)
            acc.push({
              category: 'subject',
              subject: { id, name: subj.name, archived: subj.archived },
            });
          return acc;
        }, []) ?? []
      );

    case MessageCriteriaType.Group:
      return (
        el.groups?.map((g) => ({
          category: 'group' as const,
          group: { id: g.id, name: g.name },
        })) ?? []
      );

    case MessageCriteriaType.Age_group:
      return (
        el.element_id?.reduce<MessageCriteriaItem[]>((acc, id) => {
          const ag = ageGroups?.find((g) => g.id === id);
          if (!ag) return acc;

          const level = schoolLevels?.find((l) => l.id === ag.level_id);
          const levelCount = ageGroups?.filter((g) => g.level_id === ag.level_id).length ?? 0;

          acc.push({
            category: 'age_group',
            school_property: { id, name: ag.name, archived: ag.archived },
            ...(ag.level_id && {
              propGroup: { id: ag.level_id, name: level?.name ?? '', count: levelCount },
            }),
          });
          return acc;
        }, []) ?? []
      );

    default: {
      const category = categoryMap[el.type];
      if (!category) return [];

      return (
        el.element_id?.reduce<MessageCriteriaItem[]>((acc, id) => {
          const prop = properties?.find((p) => p.id === id);
          if (prop)
            acc.push({
              category,
              school_property: { id, name: prop.name, archived: prop.archived },
            });
          return acc;
        }, []) ?? []
      );
    }
  }
};

const convertToMedicalNeedFilter = (data: MedicalNeedCriteriaData): MedicalNeedFilter => {
  const { operator, value, severity, reviewed_by_nurse } = data;

  switch (operator) {
    case 'contains':
      return {
        operator: TextOperator.Contains,
        value: value ?? '',
        severity: severity ?? undefined,
        reviewed_by_nurse: reviewed_by_nurse ?? undefined,
      };
    case 'none':
      return {
        operator: TextOperator.None,
        value: undefined,
        severity: undefined,
        reviewed_by_nurse: undefined,
      };
    case 'all':
    default:
      return {
        operator: TextOperator.All,
        value: undefined,
        severity: data.severity ?? undefined,
        reviewed_by_nurse: data.reviewed_by_nurse ?? undefined,
      };
  }
};

const convertToCustomFieldFilter = (data: CustomFieldCriteriaData): CustomFieldFilter => {
  const { type, operator, value, id } = data;

  switch (type) {
    case CustomFieldDataType.BOOLEAN:
      switch (operator) {
        case 'is_equal':
          return {
            id,
            type,
            operator: BooleanOperator.IsEqual,
            value: typeof value === 'boolean' ? value : false,
          };
        case 'none':
          return { id, type, operator: BooleanOperator.None, value: undefined };
        case 'all':
        default:
          return { id, type, operator: BooleanOperator.All, value: undefined };
      }

    case CustomFieldDataType.DATE:
      switch (operator) {
        case 'in_range':
          return {
            id,
            type,
            operator: DateOperator.InRange,
            value: Array.isArray(value) ? [value[0], value[1]] : ['', ''],
          };
        case 'is_equal':
          return {
            id,
            type,
            operator: DateOperator.IsEqual,
            value: Array.isArray(value) ? [value[0]] : [''],
          };
        case 'none':
          return { id, type, operator: DateOperator.None, value: undefined };
        case 'all':
        default:
          return { id, type, operator: DateOperator.All, value: undefined };
      }

    case CustomFieldDataType.SELECT:
    case CustomFieldDataType.ARR_STRING:
    case CustomFieldDataType.ARR_NUMBER:
      switch (operator) {
        case 'is_one_of':
          return {
            id,
            type,
            operator: SelectListOperator.IsOneOf,
            value: Array.isArray(value) ? value : [],
          };
        case 'none':
          return { id, type, operator: SelectListOperator.None, value: undefined };
        case 'all':
        default:
          return { id, type, operator: SelectListOperator.All, value: undefined };
      }

    case CustomFieldDataType.STRING:
    case CustomFieldDataType.NUMBER:
    default: {
      const textType =
        type === CustomFieldDataType.NUMBER
          ? CustomFieldDataType.NUMBER
          : CustomFieldDataType.STRING;

      switch (operator) {
        case 'contains':
          return {
            id,
            type: textType,
            operator: TextOperator.Contains,
            value: typeof value === 'string' ? value : '',
          };
        case 'none':
          return { id, type: textType, operator: TextOperator.None, value: undefined };
        case 'all':
        default:
          return { id, type: textType, operator: TextOperator.All, value: undefined };
      }
    }
  }
};

const getItemsFromCriteriaData = (
  data: MessageCriteriaData,
  customFields: CustomField[],
): MessageCriteriaItem[] => {
  const result: MessageCriteriaItem[] = [];

  if (data.expired != null) {
    result.push({ category: 'expired', expired: data.expired });
  }

  if (data.medical_needs) {
    result.push({
      category: 'medical_needs',
      medical_needs: convertToMedicalNeedFilter(data.medical_needs),
    });
  }

  if (data.custom_fields?.length) {
    for (const cf of data.custom_fields) {
      const filter = convertToCustomFieldFilter(cf);
      const definition = customFields.find((d) => d.id === cf.id);
      result.push({
        category: 'custom_fields',
        custom_field: { ...filter, name: definition?.label ?? '' },
      });
    }
  }

  return result;
};

interface ConvertMessageCriteriaToItemsParams {
  criteria: MessageCriteria[];
  properties: SchoolProperty[];
  subjects: GroupSubject[];
  ageGroups: AgeGroup[];
  schoolLevels: SchoolLevel[];
  customFields: CustomField[];
}

export const convertMessageCriteriaToItems = ({
  criteria,
  properties,
  subjects,
  ageGroups,
  schoolLevels,
  customFields,
}: ConvertMessageCriteriaToItemsParams): MessageCriteriaItem[] => {
  return criteria.flatMap((c) =>
    isMessageCriteriaWithFilterElement(c)
      ? getItemsFromFilterElement({
          el: c.filter_element,
          properties,
          subjects,
          ageGroups,
          schoolLevels,
        })
      : getItemsFromCriteriaData(c.criteria_data, customFields),
  );
};

const getSelectedCriteriaWithGrouping = (criteriaList: MessageCriteriaItem[]) => {
  const groupsSelected: string[] = [];
  const groupsNotSelected: string[] = [];

  return criteriaList.reduce<MessageCriteriaItem[]>((acc, c) => {
    if (c.propGroup?.id && groupsSelected.includes(c.propGroup.id)) return acc;

    if (!c.propGroup?.id || groupsNotSelected.includes(c.propGroup.id)) {
      acc.push(c);
      return acc;
    }

    const selectedOfGroup = criteriaList.filter((item) => item.propGroup?.id === c.propGroup?.id);
    if (selectedOfGroup.length === c.propGroup.count) {
      groupsSelected.push(c.propGroup.id);
      acc.push({
        category: c.category,
        propGroupItems: [...selectedOfGroup],
        propGroup: c.propGroup,
      });
    } else {
      groupsNotSelected.push(c.propGroup.id);
      acc.push(c);
    }

    return acc;
  }, []);
};

export const getCriteriaForMessageForm = (criteria: MessageCriteriaItem[]) => {
  return criteria.reduce<MessageFormCriteria>(
    (
      acc,
      {
        category,
        school_property,
        subject,
        group,
        enum_index,
        expired,
        medical_needs,
        custom_field,
      },
    ) => {
      switch (category) {
        case 'status':
          if (!school_property || school_property.archived) return acc;
          if (!acc.status) acc.status = [];
          acc.status.push(school_property.id);
          return acc;
        case 'age_group':
          if (!school_property || school_property.archived) return acc;
          if (!acc.age_group) acc.age_group = [];
          acc.age_group.push(school_property.id);
          return acc;
        case 'house':
          if (!school_property || school_property.archived) return acc;
          if (!acc.house) acc.house = [];
          acc.house.push(school_property.id);
          return acc;
        case 'department':
          if (!school_property || school_property.archived) return acc;
          if (!acc.department) acc.department = [];
          acc.department.push(school_property.id);
          return acc;
        case 'subject':
          if (!subject || subject.archived) return acc;
          if (!acc.subject) acc.subject = [];
          acc.subject.push(subject.id);
          return acc;
        case 'group':
          if (!group) return acc;
          if (!acc.group) acc.group = [];
          acc.group.push(group.id);
          return acc;
        case 'gender':
          if (enum_index === undefined) return acc;
          if (!acc.gender) acc.gender = [];
          acc.gender.push(enum_index);
          return acc;
        case 'nationality':
          if (enum_index === undefined) return acc;
          if (!acc.nationality) acc.nationality = [];
          acc.nationality.push(enum_index);
          return acc;
        case 'expired':
          if (expired !== undefined) acc.expired = expired;
          return acc;
        case 'medical_needs':
          if (medical_needs) acc.medical_needs = medical_needs;
          return acc;
        case 'custom_fields':
          if (custom_field) {
            if (!acc.custom_fields) acc.custom_fields = [];
            const { name: _, ...filter } = custom_field;
            acc.custom_fields.push(filter);
          }
          return acc;
      }
      return acc;
    },
    {},
  );
};

export const getIndividualIdsForMessageForm = (individuals: Individual[]): string[] => {
  return (
    individuals?.reduce<string[]>(
      (acc, { school_user_relation }) =>
        school_user_relation.relation_id ? [...acc, school_user_relation.relation_id] : acc,
      [],
    ) ?? []
  );
};
